"use client"

import { useState } from "react"
import Checkbox from "../../../../components/Checkbox"
import { FaTrash } from 'react-icons/fa';
import styles from "./css/family-history.module.css"
import { updateConsultation } from "../../../../api/clinic/consultationApi.js";

export default function FamilyHistory({ 
  consultationId, 
  patientData = {}, 
  initialData,
  onDataChange,
  ensureConsultationExists,
  onSave 
}) {
  const [familyMembers, setFamilyMembers] = useState(
    initialData?.familyMembers || [{ relationship: "", age: "", description: "", deceased: false }]
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  const notifyChange = () => {
    if (onDataChange) onDataChange()
  }

  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, { relationship: "", age: "", description: "", deceased: false }])
    notifyChange()
  }

  const removeFamilyMember = (index) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index))
    notifyChange()
  }

  const updateFamilyMember = (index, field, value) => {
    const newMembers = [...familyMembers]
    newMembers[index][field] = value
    setFamilyMembers(newMembers)
    notifyChange()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError("")
      setSaveSuccess(false)

      // Ensure consultation exists (creates if needed)
      const activeConsultationId = await ensureConsultationExists()

      // Filter out empty entries and prepare data as arrays
      const validMembers = familyMembers.filter(m => m.relationship.trim() || m.description.trim())
      const payload = {
        familyrelationship: validMembers.map(m => m.relationship),
        familyage: validMembers.map(m => parseInt(m.age) || 0),
        familydescription: validMembers.map(m => m.description),
        familydeceased: validMembers.map(m => m.deceased),
      }

      await updateConsultation(activeConsultationId, payload)

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      
      if (onSave) {
        onSave({ familyMembers })
      }
    } catch (e) {
      setSaveError(e.message || "Failed to save data")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Family History</h3>
          <button className={styles.addButton} onClick={addFamilyMember}>
            +
          </button>
        </div>

        <div className={styles.tableHeader}>
          <span className={styles.headerCell}>Relationship</span>
          <span className={styles.headerCell}>Age</span>
          <span className={styles.headerCell}>Description</span>
          <span className={styles.headerCell}>Deceased</span>
        </div>

        {familyMembers.map((member, index) => (
          <div key={index} className={styles.tableRow}>
            <input
              type="text"
              className={styles.input}
              value={member.relationship}
              onChange={(e) => updateFamilyMember(index, "relationship", e.target.value)}
            />
            <input
              type="text"
              className={styles.input}
              value={member.age}
              onChange={(e) => updateFamilyMember(index, "age", e.target.value)}
            />
            <input
              type="text"
              className={styles.input}
              value={member.description}
              onChange={(e) => updateFamilyMember(index, "description", e.target.value)}
            />
            <div className={styles.deceasedCell}>
              <Checkbox
                id={`deceased_${index}`}
                checked={member.deceased}
                onChange={(e) => updateFamilyMember(index, "deceased", e.target.checked)}
              />
            </div>
            <button className={styles.deleteButton} onClick={() => removeFamilyMember(index)}>
              <FaTrash color="red" />
            </button>
          </div>
        ))}
      </div>

      {saveError && (
        <div className={styles.inlineError}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className={styles.successMessage}>
          Data saved successfully!
        </div>
      )}

      <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  )
}